import angular from 'angular'

import { zeroIfNaN } from '../utils/bits'
import { clamp, isFinite, isElement } from 'lodash-es'

import getSize from '../utils/get-size'
import embedResize from '../utils/embed-resize'
import viewportResize from '../utils/viewport-resize'

angular
  .module('ggs.gallery-preview')
  .factory('PreviewSizeManager', PreviewSizeManagerProvider)

function PreviewSizeManagerProvider(domh, calcSizeProps) {

  const NORM_SIDE_X1 = [300, 600, 900]
  const NORM_SIDE_X2 = [600, 900, 1200]

  const QS_WRAP = '.ggs-gallery-preview'
  const QS_FOOTER = '.ggs-gallery-preview__footer'
  const QS_HEADER = '.ggs-gallery-preview__header'

  const HIGHLIGHT = true
  const NOT_HIGHLIGHT = false

  const NAIVE_WIDTH = 'naiveWidth'
  const NAIVE_HEIGHT = 'naiveHeight'
  const SQUARE_WIDTH = 'squareWidth'
  const SQUARE_HEIGHT = 'squareHeight'
  const NORMALIZE_WIDTH = 'normalizeWidth'
  const NORMALIZE_HEIGHT = 'normalizeHeight'

  return function PreviewSizeManager(
    isoGridContentElm,
    isoGridShowcaseElm,
    properties) {

    // both modes

    let infoHeightCacheText = null
    let infoHeightCacheThumb = null
    const dropInfoHeightCacheText = () => infoHeightCacheText = null
    const dropInfoHeightCacheThumb = () => infoHeightCacheThumb = null

    function getInfoHeight(pair) {
      if (pair.post.preview.info.isSomeText) {
        if (isFinite(infoHeightCacheText)) {
          return infoHeightCacheText
        }
      } else if (isFinite(infoHeightCacheThumb)) {
        return infoHeightCacheThumb
      }

      const headerElm = domh.find.notStrict(pair.elm, QS_HEADER)
      const footerElm = domh.find.notStrict(pair.elm, QS_FOOTER)
      const headerHeight = isElement(headerElm) ? getSize(headerElm).height : 0
      const footerHeight = isElement(footerElm) ? getSize(footerElm).height : 0
      const infoHeight = headerHeight + footerHeight

      if (pair.post.preview.info.isSomeText) {
        return infoHeightCacheText = infoHeight
      } else {
        return infoHeightCacheThumb = infoHeight
      }
    }

    let previewMarginYCache = null
    const dropPreviewMarginYCache = () => previewMarginYCache = null

    function getPreviewMarginY(pair) {
      if (isFinite(previewMarginYCache)) {
        return previewMarginYCache
      }

      const previewElm = domh.find(pair.elm, QS_WRAP)
      const previewSize = getSize(previewElm)
      const previewMarginY = previewSize.outerHeight - previewSize.innerHeight
      return previewMarginYCache = previewMarginY
    }

    let previewMarginXCache = null
    const dropPreviewMarginXCache = () => previewMarginXCache = null

    function getPreviewMarginX(pair) {
      if (isFinite(previewMarginXCache)) {
        return previewMarginXCache
      }

      const previewElm = domh.find(pair.elm, QS_WRAP)
      const previewSize = getSize(previewElm)
      const previewMarginX = previewSize.outerWidth - previewSize.innerWidth
      return previewMarginXCache = previewMarginX
    }

    // masonry mode

    let masonryWidthCache = null
    const dropMasonryWidthCache = () => masonryWidthCache = null

    function getMasonryWidth() {
      if (isFinite(masonryWidthCache)) {
        return masonryWidthCache
      }

      const masonryWidth = getSize(isoGridContentElm).width
      return masonryWidthCache = masonryWidth
    }

    let previewWidthCacheHighlighted = null
    let previewWidthCacheNotHighlighted = null
    const dropPreviewWidthCacheHighlighted = () => previewWidthCacheHighlighted = null
    const dropPreviewWidthCacheNotHighlighted = () => previewWidthCacheNotHighlighted = null

    function getPreviewWidth(highlight) {
      if (highlight && isFinite(previewWidthCacheHighlighted)) {
        return previewWidthCacheHighlighted
      }

      if (!highlight && isFinite(previewWidthCacheNotHighlighted)) {
        return previewWidthCacheNotHighlighted
      }

      const masonryWidth = getMasonryWidth()
      const sizeProps = calcSizeProps(masonryWidth, properties)

      const naiveWidth = Math.round(zeroIfNaN(masonryWidth / sizeProps.cols))
      const naiveDiff = masonryWidth - Math.round(naiveWidth * sizeProps.cols)
      const sizerWidth = naiveDiff > 0
        ? naiveWidth + Math.ceil(zeroIfNaN(naiveDiff / sizeProps.cols))
        : naiveWidth

      if (highlight) {
        const highlightedWidth = sizerWidth * 2
        return previewWidthCacheHighlighted = highlightedWidth <= masonryWidth ? highlightedWidth : masonryWidth
      } else {
        return previewWidthCacheNotHighlighted = sizerWidth
      }
    }

    function getMasonrySizerWidth() {
      return getPreviewWidth(NOT_HIGHLIGHT)
    }

    // carousel mode

    let carouselHeightCache = null
    const dropCarouselHeightCache = () => carouselHeightCache = null

    function getCarouselHeight() {
      if (isFinite(carouselHeightCache)) {
        return carouselHeightCache
      }

      const {
        carousel_layout_classic,
        carousel_layout_fill_viewport,
        carousel_layout_static_rows,
        carousel_fill_viewport_fraction,
        carousel_showcase_min_height } = properties

      let carouselHeight = null
      if (carousel_layout_classic) {
        const carouselWidth = getCarouselWidth()
        const sizeProps = calcSizeProps(carouselWidth, properties)
        const sizerWidth = Math.round(zeroIfNaN(carouselWidth / sizeProps.cols))
        carouselHeight = Math.round(sizerWidth * sizeProps.rows)
      } else if (carousel_layout_static_rows) {
        const vpHeight = calcViewportHeight()
        const sizeProps = calcSizeProps(vpHeight, properties)
        carouselHeight = Math.round(sizeProps.rowHeight * sizeProps.rows)
      } else if (carousel_layout_fill_viewport) {
        const vpHeight = calcViewportHeight()
        const naiveHeight = Math.round(vpHeight * carousel_fill_viewport_fraction)
        carouselHeight = clamp(naiveHeight, carousel_showcase_min_height, vpHeight)
      }

      return carouselHeightCache = carouselHeight
    }

    let carouselWidthCache = null
    const dropCarouselWidthCache = () => carouselWidthCache = null

    function getCarouselWidth() {
      if (isFinite(carouselWidthCache)) {
        return carouselWidthCache
      }

      const roundWidth = getSize(isoGridShowcaseElm).width
      return carouselWidthCache = roundWidth
    }

    let previewHeightCacheHighlighted = null
    let previewHeightCacheNotHighlighted = null
    const dropPreviewHeightCacheHighlighted = () => previewHeightCacheHighlighted = null
    const dropPreviewHeightCacheNotHighlighted = () => previewHeightCacheNotHighlighted = null

    function getPreviewHeight(highlight) {
      if (highlight && isFinite(previewHeightCacheHighlighted)) {
        return previewHeightCacheHighlighted
      }

      if (!highlight && isFinite(previewHeightCacheNotHighlighted)) {
        return previewHeightCacheNotHighlighted
      }

      const {
        carousel_layout_classic,
        carousel_layout_fill_viewport,
        carousel_layout_static_rows } = properties

      let sizerHeight = null
      if (carousel_layout_classic) {
        const carouselWidth = getCarouselWidth()
        const sizeProps = calcSizeProps(carouselWidth, properties)
        const sizerWidth = Math.round(zeroIfNaN(carouselWidth / sizeProps.cols))
        sizerHeight = sizerWidth
      } else if (carousel_layout_static_rows) {
        const vpHeight = calcViewportHeight()
        const sizeProps = calcSizeProps(vpHeight, properties)
        sizerHeight = sizeProps.rowHeight
      } else if (carousel_layout_fill_viewport) {
        const carouselHeight = getCarouselHeight()
        const sizeProps = calcSizeProps(carouselHeight, properties)
        sizerHeight = Math.round(zeroIfNaN(carouselHeight / sizeProps.rows))
      }

      if (highlight) {
        const carouselHeight = getCarouselHeight()
        const highlightedHeight = sizerHeight * 2
        return previewHeightCacheHighlighted = highlightedHeight <= carouselHeight ? highlightedHeight : sizerHeight
      } else {
        return previewHeightCacheNotHighlighted = sizerHeight
      }
    }

    function getCarouselSizerHeight() {
      return getPreviewHeight(NOT_HIGHLIGHT)
    }

    let viewportHeightCache = null
    const dropViewportHeightCache = () => viewportHeightCache = null

    function calcViewportHeight() {
      if (isFinite(viewportHeightCache)) {
        return viewportHeightCache
      }

      const {
        carousel_scrollbar_size,
        carousel_scrollbar_overlay } = properties

      const vpOffset = carousel_scrollbar_overlay ? 0 : carousel_scrollbar_size
      const vpHeight = embedResize.isPositionReady()
        ? Math.max(0, embedResize.getViewportHeight() - vpOffset)
        : Math.max(0, viewportResize.getHeight() - vpOffset)

      return viewportHeightCache = vpHeight
    }

    // main methods

    function castToX2(pair) {
      return recalc(pair, HIGHLIGHT)
    }

    function castToX1(pair) {
      return recalc(pair, NOT_HIGHLIGHT)
    }

    function dropCache() {
      dropInfoHeightCacheText()
      dropInfoHeightCacheThumb()
      dropPreviewMarginYCache()
      dropPreviewMarginXCache()
      dropMasonryWidthCache()
      dropPreviewWidthCacheHighlighted()
      dropPreviewWidthCacheNotHighlighted()
      dropCarouselHeightCache()
      dropCarouselWidthCache()
      dropPreviewHeightCacheHighlighted()
      dropPreviewHeightCacheNotHighlighted()
      dropViewportHeightCache()
    }

    function recalc(pair, highlight) {
      if (properties.square_preview) {
        return square(pair, highlight)
      } else if (properties.normalize_preview) {
        return normalize(pair, highlight)
      } else {
        return naive(pair, highlight)
      }
    }

    // square

    function square(pair, highlight) {
      return properties.carousel_mode
        ? squareWidth(pair, highlight)
        : squareHeight(pair, highlight)
    }

    function squareHeight(pair, highlight) {
      const infoHeight = getInfoHeight(pair)
      const previewWidth = getPreviewWidth(highlight)
      const previewMarginX = getPreviewMarginX(pair)
      const showcaseWidth = Math.max(0, previewWidth - previewMarginX)

      const showcaseHeight = Math.max(0, showcaseWidth - infoHeight)
      const previewHeight = previewWidth
      return setPairSize(pair, showcaseWidth, previewWidth, showcaseHeight, previewHeight, SQUARE_HEIGHT)
    }

    function squareWidth(pair, highlight) {
      const infoHeight = getInfoHeight(pair)
      const previewHeight = getPreviewHeight(highlight)
      const previewMarginY = getPreviewMarginY(pair)
      const showcaseHeight = Math.max(0, previewHeight - previewMarginY - infoHeight)

      const showcaseWidth = showcaseHeight + infoHeight
      const previewWidth = previewHeight
      return setPairSize(pair, showcaseWidth, previewWidth, showcaseHeight, previewHeight, SQUARE_WIDTH)
    }

    // normalize

    function normalize(pair, highlight) {
      return properties.carousel_mode
        ? normalizeWidth(pair, highlight)
        : normalizeHeight(pair, highlight)
    }

    function normalizeHeight(pair, highlight) {
      const infoHeight = getInfoHeight(pair)
      const previewWidth = getPreviewWidth(highlight)
      const previewMarginX = getPreviewMarginX(pair)
      const previewMarginY = getPreviewMarginY(pair)
      const showcaseWidth = Math.max(0, previewWidth - previewMarginX)

      const contentHeight = pair.calcContentHeight(showcaseWidth)
      const naivePreviewHeight = contentHeight + infoHeight + previewMarginY
      const normHeightList = highlight ? NORM_SIDE_X2 : NORM_SIDE_X1
      const normPreviewHeight = normalizeSide(naivePreviewHeight, normHeightList)

      const showcaseHeight = Math.max(0, normPreviewHeight - infoHeight - previewMarginY)
      const previewHeight = normPreviewHeight
      return setPairSize(pair, showcaseWidth, previewWidth, showcaseHeight, previewHeight, NORMALIZE_HEIGHT)
    }

    function normalizeWidth(pair, highlight) {
      const infoHeight = getInfoHeight(pair)
      const previewHeight = getPreviewHeight(highlight)
      const previewMarginX = getPreviewMarginX(pair)
      const previewMarginY = getPreviewMarginY(pair)
      const showcaseHeight = Math.max(0, previewHeight - previewMarginY - infoHeight)

      const contentWidth = pair.calcContentWidth(showcaseHeight)
      const naivePreviewWidth = contentWidth + previewMarginY
      const normWidthList = highlight ? NORM_SIDE_X2 : NORM_SIDE_X1
      const normPreviewWidth = normalizeSide(naivePreviewWidth, normWidthList)

      const showcaseWidth = Math.max(0, normPreviewWidth - previewMarginX)
      const previewWidth = normPreviewWidth
      return setPairSize(pair, showcaseWidth, previewWidth, showcaseHeight, previewHeight, NORMALIZE_WIDTH)
    }

    function normalizeSide(naiveSide, normList) {
      let minDiff = null
      let bestSide = null
      for (const side of normList) {
        const rawDiff = naiveSide - side
        const normDiff = rawDiff > 0 ? Math.floor(rawDiff / 2) : Math.abs(rawDiff)
        if ((minDiff === null) || (normDiff < minDiff)) {
          minDiff = normDiff
          bestSide = side
        } else {
          break
        }
      }
      return bestSide
    }

    // naive

    function naive(pair, highlight) {
      return properties.carousel_mode
        ? naiveWidth(pair, highlight)
        : naiveHeight(pair, highlight)
    }

    function naiveHeight(pair, highlight) {
      const infoHeight = getInfoHeight(pair)
      const previewWidth = getPreviewWidth(highlight)
      const previewMarginX = getPreviewMarginX(pair)
      const previewMarginY = getPreviewMarginY(pair)
      const showcaseWidth = Math.max(0, previewWidth - previewMarginX)

      const showcaseHeight = pair.calcContentHeight(showcaseWidth)
      const previewHeight = showcaseHeight + infoHeight + previewMarginY
      return setPairSize(pair, showcaseWidth, previewWidth, showcaseHeight, previewHeight, NAIVE_HEIGHT)
    }

    function naiveWidth(pair, highlight) {
      const infoHeight = getInfoHeight(pair)
      const previewHeight = getPreviewHeight(highlight)
      const previewMarginX = getPreviewMarginX(pair)
      const previewMarginY = getPreviewMarginY(pair)
      const showcaseHeight = Math.max(0, previewHeight - previewMarginY - infoHeight)

      const showcaseWidth = pair.calcContentWidth(showcaseHeight)
      const previewWidth = showcaseWidth + previewMarginX
      return setPairSize(pair, showcaseWidth, previewWidth, showcaseHeight, previewHeight, NAIVE_WIDTH)
    }

    // setPairSize

    function setPairSize(
      pair,
      showcaseWidth,
      previewWidth,
      showcaseHeight,
      previewHeight,
      method) {

      const psmSize = {
        previewWidth: Math.round(previewWidth),
        previewHeight: Math.round(previewHeight),
        showcaseWidth: Math.round(showcaseWidth),
        showcaseHeight: Math.round(showcaseHeight),
        method
      }

      pair.applyShowcaseSize(psmSize)
      return psmSize
    }

    // export

    return {
      castToX1,
      castToX2,
      dropCache,
      getMasonrySizerWidth,
      getCarouselSizerHeight,
      getMasonryWidth,
      getCarouselHeight,
      getCarouselWidth
    }
  }
}
